<template>
  <div class="quiz">
    <div v-if="currentQuiz && !isLoading" class="quiz__modal">
      <div class="quiz__ilu-wrapper">
        <div class="quiz__wrapper">
          <div class="quiz__titlebar">
            <TitleBar
              v-if="!hasStarted && !isLoadingModal"
              :title="contentTitle"
              :showRemainingTime="false"
              @close-modal="closeModal()"
            />
          </div>
          <div v-if="!isLoadingModal" class="quiz__content-wrapper">
            <div class="quiz__content-left">
              <img
                class="quiz__rocket"
                src="@/assets/images/INE_rocket.png"
                alt="Rocket taking off"
              />
            </div>
            <div class="quiz__content-right">
              <div>
                <h3 class="quiz__motivator">Let's Do This!</h3>
                <div class="quiz__count-wrapper">
                  <p class="quiz__count-holder">
                    <span class="quiz__count">{{ questionCount }}</span>
                  </p>
                  <p class="quiz__count-label">Questions</p>
                </div>
                <div class="quiz__count-wrapper">
                  <p class="quiz__count-holder">
                    <span class="quiz__count">{{ previousAttemptCount }}</span>
                  </p>
                  <p class="quiz__count-label">Previous attempts</p>
                </div>
              </div>
              <div class="quiz__btn-wrapper">
                <button
                  @click="closeModal()"
                  class="quiz__exit__btn btn btn--secondary"
                >
                  Exit / Close
                </button>
                <button
                  @click="openModal()"
                  class="quiz__take-quiz-btn btn btn--primary"
                >
                  {{
                    hasPreviousAttemptUnfinished ? 'Resume Quiz' : 'Start Quiz'
                  }}
                </button>
                <div
                  v-if="hasPreviousAttemptUnfinished"
                  class="quiz__last-attempt-date"
                >
                  <small
                    >From:
                    {{
                      hasPreviousAttemptUnfinished.last_updated_datetime
                        | formatDate
                    }}</small
                  >
                </div>
              </div>
            </div>
          </div>
          <div v-else class="quiz__loader-wrapper">
            <ActionLoader loaderClass="quiz__loader" />
          </div>
        </div>
      </div>
    </div>
    <div class="quiz__modal-wrapper">
      <QuizModal
        v-if="isModalShown"
        v-bind="{
          quiz: currentQuiz,
          attempt: currentAttempt,
          title: contentTitle,
          nextMedia,
          finishedQuiz,
          canStartIssue,
          parent,
          parentType,
        }"
        @heartbeat="emitHeartbeat"
        @close-modal="closeModal"
        @start-issue="startIssue"
      />
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns'
import { mapState } from 'vuex'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import TitleBar from '@/components/modals/quiz-modal/TitleBar.vue'
import QuizModal from '@/components/modals/QuizModal.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'Quiz',
  components: {
    TitleBar,
    QuizModal,
    ActionLoader,
  },
  props: {
    content: {
      type: Object,
      required: true,
    },
    userCompletionStatus: {
      type: String,
      required: true,
    },
    parentType: {
      type: String,
      required: false,
      default: () => 'isolated',
    },
    parent: {
      type: Object,
      required: false,
    },
    contentTitle: {
      type: String,
      required: true,
    },
    nextMedia: {
      type: Object,
      required: false,
    },
    canStartIssue: {
      type: Boolean,
      required: false,
      default: () => {
        return true
      },
    },
    isolated: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  filters: {
    formatDate(date) {
      return format(new Date(date), 'LLLL d, yyyy hh:mm a')
    },
  },
  data() {
    return {
      isModalShown: false,
      isLoading: true,
      isLoadingModal: false,
      hasStarted: false,
    }
  },
  computed: {
    ...mapState({
      currentQuiz: state => state.quizzes.currentQuiz,
      previousAttempts: state => state.quizzes.previousAttempts,
      currentAttempt: state => state.quizzes.currentAttempt,
      finishedQuiz: state => state.quizzes.finishedAttempt,
    }),
    questionCount() {
      const { number_of_questions: totalQuestions } = this.currentQuiz
      return totalQuestions
    },
    quizAttempt() {
      return this.currentAttempt
    },
    maxAttempts() {
      return this.currentQuiz && this.currentQuiz.max_attempts_per_user
        ? this.currentQuiz.max_attempts_per_user
        : 0
    },
    hasPreviousAttemptUnfinished() {
      if (!Array.isArray(this.previousAttempts)) return null
      const pendingAttempt = this.previousAttempts.filter(
        a => a.status === 'pending'
      )
      return pendingAttempt ? pendingAttempt[0] : null
    },
    previousAttemptCount() {
      if (!Array.isArray(this.previousAttempts)) return 0
      return this.previousAttempts.filter(
        a => a.status === CONSTANTS.CONTENT_USER_STATUS.FINISHED
      ).length
    },
  },
  methods: {
    emitHeartbeat(payload) {
      this.$emit('heartbeat', payload)
    },
    async openModal() {
      const parentId = this?.parent?.id ?? ''
      const parentType = this?.parentType ?? ''
      this.hasStarted = true
      this.isLoadingModal = true
      this.$emit('is-loading', true)
      if (this.hasPreviousAttemptUnfinished) {
        this.$store.dispatch(
          'quizzes/resumeQuizAttempt',
          this.hasPreviousAttemptUnfinished
        )
      } else {
        await this.$store.dispatch(
          'quizzes/startQuizAttemptByQuizId',
          this.content.id
        )
      }
      this.$emit('is-loading', false)
      this.isLoadingModal = false
      this.isModalShown = true
      if (this.parentType === 'course') {
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_QUIZ_STARTED, {
          course_id: this.parent.id,
          course_name: this.parent.name,
          quiz_id: this.currentQuiz.id,
          ...this.currentQuiz,
        })
      }
    },
    closeModal() {
      this.$store.dispatch('content/setCurrentQuizId', '')
      this.$emit('toggle-quiz')
      this.isModalShown = false
    },
    startIssue(payload) {
      this.$store.dispatch('content/saveContentIssue', {
        content_type: 'quiz',
        content_name: this.contentTitle,
        content_id: this.content.id,
        extra_args: {
          ...payload,
        },
      })
    },
  },
  async created() {
    this.isLoading = true
    this.$emit('is-loading', true)
    await this.$store.dispatch('quizzes/getQuizById', this.content.id)
    await this.$store.dispatch(
      'quizzes/getQuizAttemptsByQuizId',
      this.content.id
    )
    const body = document.querySelector('body')
    body.classList.add('modal-open')
    this.$emit('is-loading', false)
    this.isLoading = false
  },
  destroyed() {
    const body = document.querySelector('body')
    body.classList.remove('modal-open')
    this.$store.dispatch('quizzes/clearQuizStore')
  },
}
</script>

<style lang="scss">
.quiz {
  color: var(--white);
  &__modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--dark-gray) 0% 0% no-repeat padding-box;
    z-index: 203;
    overflow-y: auto;
  }
  &__ilu-wrapper {
    position: relative;
    overflow: hidden;
    height: 100%;
    &:before {
      content: '';
      position: absolute;
      top: -50%;
      left: -24%;
      width: 840px;
      height: 1025px;
      transform: matrix(0, 1, -1, 0, 0, 0);
      background: transparent
        radial-gradient(closest-side at 50% 50%, #db28f2 0%, #8629f200 100%) 0%
        0% no-repeat padding-box;
      opacity: 0.14;
      z-index: -1;
    }
    &:after {
      content: '';
      position: absolute;
      bottom: -50%;
      right: -15%;
      width: 840px;
      height: 1025px;
      transform: matrix(0, 1, -1, 0, 0, 0);
      background: transparent
        radial-gradient(closest-side at 50% 50%, #db28f2 0%, #8629f200 100%) 0%
        0% no-repeat padding-box;
      opacity: 0.14;
      z-index: -1;
    }
  }
  &__wrapper {
    width: 100%;
    height: 100%;
    max-width: 100vw;
    z-index: 204;
    position: relative;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    overflow: auto;
  }
  &__content-wrapper {
    display: flex;
    position: relative;
    flex-wrap: wrap;
    max-width: 1200px;
    min-height: 100%;
    margin: 30px auto 0 auto;
    background: #252525 0% 0% no-repeat padding-box;
    box-shadow: 6px 0px 50px #0000001a;
    border-radius: 10px;
    overflow: hidden;
    align-self: flex-start;
    @include tablet {
      min-height: unset;
      padding: 50px 75px;
      margin: 30px auto 30px auto;
      flex-wrap: nowrap;
    }
    &:before {
      content: '';
      position: absolute;
      top: -30%;
      left: -24%;
      width: 667px;
      height: 711px;
      transform: matrix(0, 1, -1, 0, 0, 0);
      background: transparent
        radial-gradient(closest-side at 50% 50%, #215bc7 0%, #2113b100 100%) 0%
        0% no-repeat padding-box;
      opacity: 0.28;
    }
  }
  &__titlebar {
    width: 100%;
    align-self: flex-start;
  }
  &__content-left {
    width: 100%;
    padding: 20px;
    text-align: center;
    @include tablet {
      margin: 0;
      width: 30%;
    }
  }
  &__rocket {
    width: 200px;
    @include tablet {
      max-width: 290px;
    }
  }
  &__content-right {
    width: 100%;
    padding: 20px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    flex: 1;
    align-items: center;
    @include tablet {
      padding: 0 50px 0 50px;
      width: 70%;
      margin: -30px 0 0 0;
      -ms-flex-wrap: nowrap;
      flex-wrap: wrap;
      -webkit-box-pack: start;
      -ms-flex-pack: start;
      justify-content: flex-start;
    }
  }
  &__motivator {
    text-transform: uppercase;
    font-family: var(--font-medium);
    letter-spacing: 0;
    white-space: nowrap;
    font-size: 2rem;
    @include tablet {
      font-size: 40px;
    }
  }
  &__count-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
  }
  &__count-holder {
    display: flex;
    margin: 0;
  }
  &__count {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0 3px;
    @include tablet {
      font-size: 32px;
    }
  }
  &__count-label {
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 0 30px;
  }
  &__btn-wrapper {
    display: flex;
    width: 100%;
    justify-content: center;
    align-content: center;
    flex-wrap: wrap;
    @include tablet {
      margin: auto 0 0 0;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: flex-end;
      width: 100%;
      justify-content: flex-end;
    }
  }
  &__loader-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
  }
  &__loader {
    width: 5rem;
  }
  &__last-attempt-date {
    width: 100%;
    text-align: right;
    margin-top: 10px;
  }
  &__exit__btn {
    margin: 0px 10px;
  }
}
</style>
